import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart3, ChevronRight, RefreshCw, Loader2,
  TrendingUp, TrendingDown, Fuel, Coins, DollarSign, Landmark,
  Clock, Activity,
} from 'lucide-react'
import { clsx } from 'clsx'
import { getMarketsOverview } from '@/lib/api'
import type {
  MarketsOverviewData,
  OverviewIndex,
  OverviewVolatility,
  OverviewSector,
  OverviewFearGreed,
  OverviewYieldPoint,
} from '@/lib/types'

// ── Exchange definitions with real trading hours ────────────────────────

interface ExchangeDef {
  fullName: string
  city: string
  abbreviation: string
  flag: string
  timezone: string
  openHour: number
  openMinute: number
  closeHour: number
  closeMinute: number
  preMarketMinutes: number
}

const EXCHANGES: ExchangeDef[] = [
  { fullName: 'Australian Securities Exchange', city: 'Sydney',    abbreviation: 'ASX',   flag: '\u{1F1E6}\u{1F1FA}', timezone: 'Australia/Sydney',  openHour: 10, openMinute: 0,  closeHour: 16, closeMinute: 0,  preMarketMinutes: 60 },
  { fullName: 'Tokyo Stock Exchange',           city: 'Tokyo',     abbreviation: 'TSE',   flag: '\u{1F1EF}\u{1F1F5}', timezone: 'Asia/Tokyo',       openHour: 9,  openMinute: 0,  closeHour: 15, closeMinute: 0,  preMarketMinutes: 60 },
  { fullName: 'Korea Exchange',                 city: 'Seoul',     abbreviation: 'KRX',   flag: '\u{1F1F0}\u{1F1F7}', timezone: 'Asia/Seoul',       openHour: 9,  openMinute: 0,  closeHour: 15, closeMinute: 30, preMarketMinutes: 60 },
  { fullName: 'Shanghai Stock Exchange',        city: 'Shanghai',  abbreviation: 'SSE',   flag: '\u{1F1E8}\u{1F1F3}', timezone: 'Asia/Shanghai',    openHour: 9,  openMinute: 30, closeHour: 15, closeMinute: 0,  preMarketMinutes: 60 },
  { fullName: 'Hong Kong Stock Exchange',       city: 'Hong Kong', abbreviation: 'HKEX',  flag: '\u{1F1ED}\u{1F1F0}', timezone: 'Asia/Hong_Kong',   openHour: 9,  openMinute: 30, closeHour: 16, closeMinute: 0,  preMarketMinutes: 60 },
  { fullName: 'Bombay Stock Exchange',          city: 'Mumbai',    abbreviation: 'BSE',   flag: '\u{1F1EE}\u{1F1F3}', timezone: 'Asia/Kolkata',     openHour: 9,  openMinute: 15, closeHour: 15, closeMinute: 30, preMarketMinutes: 60 },
  { fullName: 'SIX Swiss Exchange',             city: 'Zurich',    abbreviation: 'SIX',   flag: '\u{1F1E8}\u{1F1ED}', timezone: 'Europe/Zurich',    openHour: 9,  openMinute: 0,  closeHour: 17, closeMinute: 30, preMarketMinutes: 60 },
  { fullName: 'Frankfurt Stock Exchange',       city: 'Frankfurt', abbreviation: 'FSE',   flag: '\u{1F1E9}\u{1F1EA}', timezone: 'Europe/Berlin',    openHour: 9,  openMinute: 0,  closeHour: 17, closeMinute: 30, preMarketMinutes: 60 },
  { fullName: 'Bolsa de Madrid',                city: 'Madrid',    abbreviation: 'BME',   flag: '\u{1F1EA}\u{1F1F8}', timezone: 'Europe/Madrid',    openHour: 9,  openMinute: 0,  closeHour: 17, closeMinute: 30, preMarketMinutes: 60 },
  { fullName: 'Euronext Paris',                 city: 'Paris',     abbreviation: 'ENX',   flag: '\u{1F1EB}\u{1F1F7}', timezone: 'Europe/Paris',     openHour: 9,  openMinute: 0,  closeHour: 17, closeMinute: 30, preMarketMinutes: 60 },
  { fullName: 'London Stock Exchange',          city: 'London',    abbreviation: 'LSE',   flag: '\u{1F1EC}\u{1F1E7}', timezone: 'Europe/London',    openHour: 8,  openMinute: 0,  closeHour: 16, closeMinute: 30, preMarketMinutes: 60 },
  { fullName: 'New York Stock Exchange',        city: 'New York',  abbreviation: 'NYSE',  flag: '\u{1F1FA}\u{1F1F8}', timezone: 'America/New_York', openHour: 9,  openMinute: 30, closeHour: 16, closeMinute: 0,  preMarketMinutes: 90 },
]

// ── Session helpers ─────────────────────────────────────────────────────

function getLocalTime(timezone: string, now: Date) {
  const f = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false })
  const df = new Intl.DateTimeFormat('en-GB', { timeZone: timezone, weekday: 'short' })
  const parts = f.formatToParts(now)
  const hours = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10)
  const minutes = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10)
  const dayStr = df.format(now)
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  return {
    hours, minutes,
    dayOfWeek: dayMap[dayStr] ?? 0,
    formatted: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
  }
}

function toMinutes(h: number, m: number) { return h * 60 + m }

function computeStatus(ex: ExchangeDef, now: Date): 'OPEN' | 'PRE-MKT' | 'CLOSED' {
  const local = getLocalTime(ex.timezone, now)
  const curr = toMinutes(local.hours, local.minutes)
  const open = toMinutes(ex.openHour, ex.openMinute)
  const close = toMinutes(ex.closeHour, ex.closeMinute)
  const pre = open - ex.preMarketMinutes
  const isWeekend = local.dayOfWeek === 0 || local.dayOfWeek === 6
  if (isWeekend) return 'CLOSED'
  if (curr >= open && curr < close) return 'OPEN'
  if (curr >= pre && curr < open) return 'PRE-MKT'
  return 'CLOSED'
}

/** Returns a human-readable countdown string like "2h 05m" or "45m" */
function fmtCountdown(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`
  return `${m}m`
}

/** Minutes remaining until close (OPEN/PRE-MKT) or until open (CLOSED).
 *  Weekend-aware: on Saturday adds 2 days, on Sunday adds 1 day to reach Monday open. */
function minutesUntilEvent(ex: ExchangeDef, status: 'OPEN' | 'PRE-MKT' | 'CLOSED', now: Date): number {
  const local = getLocalTime(ex.timezone, now)
  const curr = toMinutes(local.hours, local.minutes)
  const open = toMinutes(ex.openHour, ex.openMinute)
  const close = toMinutes(ex.closeHour, ex.closeMinute)
  if (status === 'OPEN') return Math.max(0, close - curr)
  if (status === 'PRE-MKT') return Math.max(0, open - curr)

  // CLOSED — calculate days until next trading day (skip weekends)
  const dow = local.dayOfWeek // 0=Sun, 6=Sat
  let extraDays = 0
  if (dow === 6) extraDays = 2       // Saturday → Monday
  else if (dow === 0) extraDays = 1  // Sunday → Monday
  else if (curr >= close) extraDays = 1 // After close on weekday → tomorrow

  // If Friday after close, skip to Monday (2 extra days)
  if (dow === 5 && curr >= close) extraDays = 3

  if (extraDays === 0 && curr < open - ex.preMarketMinutes) {
    return Math.max(0, open - curr)
  }
  return Math.max(0, extraDays * 1440 - curr + open)
}

// ── Formatting ──────────────────────────────────────────────────────────

// The backend deliberately emits change/change_pct as null when the upstream
// quote lacks them (see markets_overview._quote) — every formatter must render
// a dash, never call .toFixed on null (one null row white-screened the page).
function fmtPrice(n: number | null | undefined, decimals = 2) {
  if (n == null) return '—'
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function fmtPct(n: number | null | undefined) {
  if (n == null) return '—'
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
}

function fmtChange(n: number | null | undefined, decimals = 2) {
  if (n == null) return '—'
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}`
}

// ── Region tabs ─────────────────────────────────────────────────────────

const REGION_TABS = ['All', 'Americas', 'Europe', 'Asia Pacific'] as const

// ── Per-index home exchange ─────────────────────────────────────────────
// Abbreviations must match EXCHANGES above. Asia-Pacific is NOT one session —
// ASX / BSE / KRX / SSE / HKEX each keep their own hours (TSE is Japan only).
// ChiNext trades on Shenzhen, which shares Shanghai's session times.
const INDEX_KEY_TO_EXCHANGE: Record<string, string> = {
  // Europe
  ftse100: 'LSE', dax40: 'FSE', cac40: 'ENX', ibex35: 'BME', smi: 'SIX',
  // Asia Pacific
  nikkei225: 'TSE',
  asx200: 'ASX',
  sensex: 'BSE',
  kospi: 'KRX',
  hangseng: 'HKEX', hstech: 'HKEX',
  sse_composite: 'SSE', csi300: 'SSE', chinext: 'SSE', star50: 'SSE', sse50: 'SSE', csi500: 'SSE',
}

// The card deep-links to the NASDAQ-100 (^NDX) detail page, so label the card
// as the destination rather than the ^IXIC composite the backend quotes.
const INDEX_DISPLAY_OVERRIDES: Record<string, { name: string; short: string }> = {
  nasdaq100: { name: 'NASDAQ-100 (^NDX)', short: 'NDX' },
}

// ── Component ───────────────────────────────────────────────────────────

export default function IndicesPage() {
  const navigate = useNavigate()
  const [now, setNow] = useState(() => new Date())
  const [data, setData] = useState<MarketsOverviewData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [regionTab, setRegionTab] = useState<string>('All')
  const [sparkHistory, setSparkHistory] = useState<Record<string, number[]>>(() => loadSparklineHistory())

  // Tick clock every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 10_000)
    return () => clearInterval(interval)
  }, [])

  const fetchData = useCallback(async (retry = true) => {
    setLoading(true)
    setError(null)
    try {
      const result = await getMarketsOverview()
      setData(result)
      if (result.indices?.length) {
        // Prefer backend sparkline data (from Redis price history).
        // Fall back to client-side accumulation if backend doesn't provide it.
        const hasBackendSpark = result.indices.some((i: any) => i.sparkline?.length > 1)
        if (hasBackendSpark) {
          const hist: Record<string, number[]> = {}
          for (const i of result.indices as any[]) {
            if (i.sparkline?.length > 1) hist[i.short] = i.sparkline
          }
          setSparkHistory(hist)
        } else {
          const updated = appendSparklinePoints(result.indices.map(i => ({ short: i.short, price: i.price, change: i.change })))
          setSparkHistory(updated)
        }
      }
    } catch (e) {
      // Retry once after 3 seconds
      if (retry) {
        setTimeout(() => fetchData(false), 3000)
        return
      }
      setError('Market data temporarily unavailable — exchange sessions are live')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Backend returns status 'warming' | 'cache_cold' with empty arrays while the
  // price cache warms — show a banner and re-poll every 15s until it's 'warm'.
  const warming = data != null && (data.status ?? 'warm') !== 'warm'
  useEffect(() => {
    if (!warming) return
    const interval = setInterval(() => { fetchData(false) }, 15_000)
    return () => clearInterval(interval)
  }, [warming, fetchData])

  // Compute exchange sessions client-side
  const sessions = useMemo(() => {
    return EXCHANGES.map(ex => {
      const status = computeStatus(ex, now)
      const minsLeft = minutesUntilEvent(ex, status, now)
      const local = getLocalTime(ex.timezone, now)
      const isWeekend = local.dayOfWeek === 0 || local.dayOfWeek === 6
      // Weekend: show "Mon HH:MM" instead of raw countdown
      let countdown = fmtCountdown(minsLeft)
      if (isWeekend && status === 'CLOSED') {
        const h = Math.floor(minsLeft / 60)
        if (h >= 24) {
          const days = Math.floor(h / 24)
          countdown = `Mon ${String(ex.openHour).padStart(2,'0')}:${String(ex.openMinute).padStart(2,'0')} (${days}d ${h % 24}h)`
        }
      }
      return {
        ...ex,
        status,
        localTime: local.formatted,
        minsLeft,
        countdownLabel: countdown,
        tradingHours: `${String(ex.openHour).padStart(2,'0')}:${String(ex.openMinute).padStart(2,'0')} – ${String(ex.closeHour).padStart(2,'0')}:${String(ex.closeMinute).padStart(2,'0')}`,
      }
    })
  }, [now])

  // Filter indices by region
  const filteredIndices = useMemo(() => {
    if (!data) return []
    if (regionTab === 'All') return data.indices
    return data.indices.filter(i => i.region === regionTab)
  }, [data, regionTab])

  const goChart = (ticker: string) => navigate(`/app/chart?ticker=${ticker}`)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <BarChart3 size={18} className="text-orange-400" />
          </div>
          <div>
            <h1 className="page-title">Markets Overview</h1>
            <p className="text-xs text-text-muted">
              All asset classes &mdash; indices, commodities, forex, crypto, bonds
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchData()}
          className="btn-secondary px-2.5 py-2"
          aria-label="Refresh"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* ── Market Sessions ───────────────────────────────────────── */}
        <ExchangeSessions sessions={sessions} indices={data?.indices} />

        {/* Loading state */}
        {loading && !data && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" aria-label="Loading market data">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="skeleton h-28" />)}
          </div>
        )}

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-3 text-rose-400 text-sm">
            {error}
          </div>
        )}

        {/* Cache warming — backend returned empty arrays; re-polling every 15s */}
        {warming && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 text-amber-400 text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin shrink-0" />
            Market data warming up — refreshing shortly
          </div>
        )}

        {data && (
          <>
            {/* ── Global Indices ──────────────────────────────────────── */}
            <section>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <h2 className="panel-title flex items-center gap-2">
                  <TrendingUp size={14} /> Global Indices
                </h2>
                <div className="glass rounded-lg p-1 flex items-center gap-1 flex-wrap">
                  {REGION_TABS.map(tab => (
                    <button
                      type="button"
                      key={tab}
                      onClick={() => setRegionTab(tab)}
                      className={clsx(
                        'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                        regionTab === tab
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'text-text-muted hover:bg-surface-card hover:text-text-primary',
                      )}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 stagger-children">
                {filteredIndices.map(idx => {
                  // Determine if the home exchange for this index is closed.
                  // Per-index mapping first (Asia-Pacific is NOT all Tokyo);
                  // fall back to a region default, and omit session state when
                  // the home exchange has no session definition.
                  const primaryEx = INDEX_KEY_TO_EXCHANGE[idx.key]
                    ?? ({ 'Americas': 'NYSE', 'Europe': 'LSE' } as Record<string, string>)[idx.region]
                  const session = primaryEx ? sessions.find(s => s.abbreviation === primaryEx) : undefined
                  const isClosed = session ? session.status === 'CLOSED' : undefined
                  const display = INDEX_DISPLAY_OVERRIDES[idx.key]
                  return (
                  <IndexCard key={idx.symbol} idx={display ? { ...idx, ...display } : idx} sparkPoints={sparkHistory[idx.short] ?? []} closed={isClosed} onClick={() => {
                    const TRIGGER_KEYS = new Set([
                      'sp500','nasdaq100','dow30',
                      'ftse100','dax40','cac40','ibex35','smi',
                      'nikkei225','hangseng','kospi','sensex','asx200',
                      'csi300','chinext','star50','sse50',
                    ])
                    if (TRIGGER_KEYS.has(idx.key)) {
                      navigate(`/app/indices/${idx.key}`)
                    } else {
                      goChart(idx.symbol)
                    }
                  }} />
                  )
                })}
              </div>
            </section>

            {/* ── Volatility & Fear ───────────────────────────────────── */}
            {data.volatility?.length > 0 && (
              <section>
                <h2 className="panel-title flex items-center gap-2 mb-3">
                  <Activity size={14} /> Volatility &amp; Fear
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                  {data.volatility.map(v => (
                    <VolatilityCard key={v.symbol} v={v} />
                  ))}
                </div>
              </section>
            )}

            {/* ── Fear & Greed Index ───────────────────────────────────── */}
            {data.fear_greed && (
              <FearGreedCard fg={data.fear_greed} />
            )}

            {/* ── S&P Sector Performance ───────────────────────────────── */}
            {data.sectors?.length > 0 && (
              <SectorHeatmap sectors={data.sectors} />
            )}

            {/* ── Commodities ─────────────────────────────────────────── */}
            <section>
              <h2 className="panel-title flex items-center gap-2 mb-3">
                <Fuel size={14} /> Commodities
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
                {data.commodities.map(c => (
                  <AssetTile
                    key={c.symbol}
                    label={c.name}
                    value={fmtPrice(c.price, c.price < 10 ? 4 : 2)}
                    change={c.change}
                    changePct={c.change_pct}
                    onClick={() => goChart(c.symbol)}
                  />
                ))}
              </div>
            </section>

            {/* ── Forex ───────────────────────────────────────────────── */}
            <section>
              <h2 className="panel-title flex items-center gap-2 mb-3">
                <DollarSign size={14} /> Forex
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
                {data.forex.map(f => (
                  <AssetTile
                    key={f.symbol}
                    label={f.pair}
                    value={fmtPrice(f.rate, 4)}
                    change={f.change}
                    changePct={f.change_pct}
                    onClick={() => goChart(f.symbol)}
                  />
                ))}
              </div>
            </section>

            {/* ── Crypto ──────────────────────────────────────────────── */}
            <section>
              <h2 className="panel-title flex items-center gap-2 mb-3">
                <Coins size={14} /> Crypto
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
                {data.crypto.map(c => (
                  <AssetTile
                    key={c.symbol}
                    label={c.code}
                    sublabel={c.name}
                    value={c.price >= 1 ? fmtPrice(c.price, 2) : fmtPrice(c.price, 4)}
                    change={c.change}
                    changePct={c.change_pct}
                    onClick={() => goChart(c.symbol)}
                  />
                ))}
              </div>
            </section>

            {/* ── Bond Yields ─────────────────────────────────────────── */}
            <section>
              <h2 className="panel-title flex items-center gap-2 mb-3">
                <Landmark size={14} /> Bond Yields
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {data.bonds.map(b => (
                  <div
                    key={b.symbol}
                    className="card p-4 hover:border-orange-500/30 transition-colors"
                  >
                    <p className="text-xs text-text-muted mb-1">{b.name}</p>
                    <p className="text-lg font-bold text-text-primary font-mono">
                      {(b.yield_value ?? 0).toFixed(3)}%
                    </p>
                    <p className={clsx(
                      'text-xs font-mono mt-1',
                      (b.change ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400',
                    )}>
                      {/* change arrives in percentage points (0.05 = 5 bps) — ×100 for bps */}
                      {fmtChange(b.change == null ? null : b.change * 100, 1)} bps
                    </p>
                  </div>
                ))}
              </div>
              {/* Yield curve chart + spread */}
              {data.yield_curve && data.yield_curve.length >= 2 && (
                <YieldCurveChart points={data.yield_curve} />
              )}
              {data.yield_spread != null && !data.yield_curve?.length && (
                <div className="mt-2 px-3 py-2 card rounded-lg inline-flex items-center gap-2 text-xs">
                  <span className="text-text-muted">10Y-3M Spread:</span>
                  <span className={clsx(
                    'font-mono font-bold',
                    data.yield_spread >= 0 ? 'text-emerald-400' : 'text-rose-400',
                  )}>
                    {(data.yield_spread ?? 0).toFixed(3)}%
                  </span>
                  {data.yield_spread < 0 && (
                    <span className="text-amber-400 text-[10px] font-medium px-1.5 py-0.5 bg-amber-500/10 rounded">
                      INVERTED
                    </span>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  )
}

// ── Exchange Sessions component ─────────────────────────────────────────

type SessionEntry = ExchangeDef & {
  status: 'OPEN' | 'PRE-MKT' | 'CLOSED'
  localTime: string
  minsLeft: number
  countdownLabel: string
  tradingHours: string
}

// Map exchanges to their primary index for display on cards
const EXCHANGE_INDEX_MAP: Record<string, string> = {
  ASX: 'ASX 200', TSE: 'Nikkei 225', KRX: 'KOSPI', SSE: 'CSI 300',
  HKEX: 'Hang Seng', BSE: 'BSE Sensex', SIX: 'SMI', FSE: 'DAX 40',
  BME: 'IBEX 35', ENX: 'CAC 40', LSE: 'FTSE 100', NYSE: 'S&P 500',
}

function ExchangeSessions({ sessions, indices }: { sessions: SessionEntry[]; indices?: Array<{ name: string; price: number; change: number | null; change_pct: number | null }> }) {
  const open    = sessions.filter(s => s.status === 'OPEN')
  const preMkt  = sessions.filter(s => s.status === 'PRE-MKT')
  const closed  = sessions.filter(s => s.status === 'CLOSED')

  return (
    <div className="space-y-6">
      {/* ── Open markets ── */}
      {open.length > 0 && (
        <div>
          <h2 className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse inline-block" />
            Open Markets
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {open.map(s => {
              const idxName = EXCHANGE_INDEX_MAP[s.abbreviation]
              const idx = indices?.find(i => i.name === idxName)
              const up = idx ? (idx.change ?? 0) >= 0 : true
              return (
              <div
                key={s.abbreviation}
                className="bg-emerald-500/5 border border-emerald-500/40 rounded-xl p-4 flex flex-col gap-1"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-base font-bold text-text-primary leading-tight">{s.fullName}</p>
                    <p className="text-xs text-text-muted mt-0.5">{s.flag} {s.city}</p>
                  </div>
                  <span className="chip-live shrink-0">
                    OPEN
                  </span>
                </div>
                {/* Index value + change */}
                {idx && (
                  <div className="mt-2 flex items-baseline gap-3">
                    <span className="text-lg font-bold font-mono text-text-primary">
                      {(idx.price ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className={clsx('text-sm font-bold font-mono', up ? 'text-emerald-400' : 'text-rose-400')}>
                      {up ? '+' : ''}{(idx.change ?? 0).toFixed(0)} ({up ? '+' : ''}{(idx.change_pct ?? 0).toFixed(2)}%)
                    </span>
                    <span className="text-xs text-text-muted">{idxName}</span>
                  </div>
                )}
                <div className="flex items-end justify-between mt-1">
                  <div>
                    <p className="text-2xl font-bold font-mono text-text-primary leading-none">{s.localTime}</p>
                    <p className="text-[11px] text-text-muted font-mono mt-1">
                      <Clock size={10} className="inline mr-1 opacity-60" />
                      {s.tradingHours}
                    </p>
                  </div>
                  <p className="text-sm text-emerald-400 font-medium text-right">
                    closes in {s.countdownLabel}
                  </p>
                </div>
              </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Pre-market ── */}
      {preMkt.length > 0 && (
        <div>
          {open.length > 0 && <hr className="border-border/30 mb-6" />}
          <h2 className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
            Pre-Market
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {preMkt.map(s => (
              <div
                key={s.abbreviation}
                className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4 flex flex-col gap-1"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-base font-bold text-text-primary leading-tight">{s.fullName}</p>
                    <p className="text-xs text-text-muted mt-0.5">{s.flag} {s.city}</p>
                  </div>
                  <span className="badge-amber shrink-0 uppercase tracking-wide font-bold">
                    PRE-MKT
                  </span>
                </div>
                <div className="flex items-end justify-between mt-2">
                  <div>
                    <p className="text-3xl font-bold font-mono text-text-primary leading-none">{s.localTime}</p>
                    <p className="text-[11px] text-text-muted font-mono mt-1">
                      <Clock size={10} className="inline mr-1 opacity-60" />
                      {s.tradingHours}
                    </p>
                  </div>
                  <p className="text-sm text-amber-400 font-medium text-right">
                    opens in {s.countdownLabel}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Closed markets ── */}
      {closed.length > 0 && (
        <div>
          {(open.length > 0 || preMkt.length > 0) && <hr className="border-border/30 mb-6" />}
          <h2 className="text-xs font-bold text-text-muted uppercase tracking-widest mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-text-muted inline-block" />
            Closed Markets
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {closed.map(s => (
              <div
                key={s.abbreviation}
                className="bg-surface-card/50 border border-border/50 rounded-xl p-4 flex flex-col gap-1"
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-text-secondary leading-tight">{s.fullName}</p>
                    <p className="text-xs text-text-muted mt-0.5">{s.flag} {s.city}</p>
                  </div>
                  <span className="badge-neutral shrink-0 ml-2 uppercase tracking-wide font-bold opacity-70">
                    CLOSED
                  </span>
                </div>
                <p className="text-xl font-bold font-mono text-text-primary leading-none">{s.localTime}</p>
                <p className="text-sm font-bold text-amber-400 mt-1">
                  <Clock size={11} className="inline mr-1 opacity-70" />
                  opens in {s.countdownLabel}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sparkline localStorage helpers ─────────────────────────────────────

const SPARKLINE_KEY = 'av-sparkline-history'
const SPARKLINE_MAX = 20

function loadSparklineHistory(): Record<string, number[]> {
  try {
    const raw = localStorage.getItem(SPARKLINE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveSparklineHistory(history: Record<string, number[]>) {
  try {
    localStorage.setItem(SPARKLINE_KEY, JSON.stringify(history))
  } catch {
    // localStorage quota or unavailable — silently skip
  }
}

function appendSparklinePoints(indices: Array<{ short: string; price: number; change?: number | null }>) {
  const history = loadSparklineHistory()
  for (const idx of indices) {
    const key = idx.short
    const pts = history[key] ?? []
    // Seed with computed open price so sparklines render on first visit
    if (pts.length === 0 && idx.change != null && idx.change !== 0) {
      pts.push(idx.price - idx.change)
    }
    pts.push(idx.price)
    if (pts.length > SPARKLINE_MAX) pts.splice(0, pts.length - SPARKLINE_MAX)
    history[key] = pts
  }
  saveSparklineHistory(history)
  return history
}

// ── Mini SVG Sparkline ──────────────────────────────────────────────────

function MiniSparkline({ points, up, closed }: { points: number[]; up: boolean; closed?: boolean }) {
  if (points.length < 2) return null
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const w = 64, h = 20
  const coords = points.map((p, i) =>
    `${(i / (points.length - 1)) * w},${h - ((p - min) / range) * h}`
  ).join(' ')
  // Blue for closed markets (previous day data), green/red for live
  const color = closed ? '#3b82f6' : up ? '#10b981' : '#ef4444'
  return (
    <svg width={w} height={h} className="shrink-0">
      <polyline points={coords} fill="none"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        opacity={closed ? 0.6 : 1} />
    </svg>
  )
}

// ── Index Card sub-component ────────────────────────────────────────────

function IndexCard({ idx, sparkPoints, closed, onClick }: { idx: OverviewIndex; sparkPoints: number[]; closed?: boolean; onClick: () => void }) {
  const positive = (idx.change_pct ?? 0) >= 0
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'group bg-surface-card border rounded-xl p-4 text-left hover-lift cursor-pointer w-full',
        positive ? 'border-emerald-500/30 hover:border-emerald-500/60' : 'border-rose-500/30 hover:border-rose-500/60',
      )}
    >
      {/* Top row: ticker badge + chevron */}
      <div className="flex items-center justify-between mb-2">
        <span className={clsx(
          'text-[11px] font-bold px-2 py-0.5 rounded-full',
          positive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400',
        )}>
          {idx.short}
        </span>
        <ChevronRight size={13} className="text-text-muted group-hover:text-orange-400 transition-colors" />
      </div>

      {/* Index name */}
      <h3 className="text-sm font-semibold text-text-secondary truncate leading-tight">{idx.name}</h3>

      {/* Value — prominent */}
      <p className="text-2xl font-bold font-mono text-text-primary mt-2 leading-none tracking-tight">
        {fmtPrice(idx.price, idx.price > 1000 ? 0 : 2)}
      </p>

      {/* Change row + sparkline */}
      <div className="flex items-center justify-between mt-2 gap-2">
        <div className={clsx(
          'gap-1.5',
          positive ? 'delta-chip-pos' : 'delta-chip-neg',
        )}>
          {positive
            ? <TrendingUp size={12} className="text-emerald-400 shrink-0" />
            : <TrendingDown size={12} className="text-rose-400 shrink-0" />}
          <span className={clsx('text-xs font-mono font-semibold', positive ? 'text-emerald-400' : 'text-rose-400')}>
            {fmtChange(idx.change, idx.price > 1000 ? 0 : 2)}
          </span>
          <span className={clsx('text-xs font-mono', positive ? 'text-emerald-300' : 'text-rose-300')}>
            ({fmtPct(idx.change_pct)})
          </span>
        </div>
        <MiniSparkline points={sparkPoints} up={positive} closed={closed} />
      </div>

      <p className="text-[10px] text-text-muted mt-2 uppercase tracking-wide">{idx.region}</p>
    </button>
  )
}

// ── Volatility Card sub-component ───────────────────────────────────────

function vixRegime(value: number): { label: string; color: string; bg: string; border: string; pulse: boolean } {
  if (value < 12)  return { label: 'Low Vol',      color: 'text-emerald-400',  bg: 'bg-emerald-500/5',   border: 'border-emerald-500/30', pulse: false }
  if (value < 20)  return { label: 'Normal',        color: 'text-emerald-300',  bg: 'bg-emerald-500/5',   border: 'border-emerald-500/20', pulse: false }
  if (value < 30)  return { label: 'Elevated',      color: 'text-amber-400',    bg: 'bg-amber-500/5',     border: 'border-amber-500/30',   pulse: false }
  if (value < 40)  return { label: 'High Fear',     color: 'text-rose-400',     bg: 'bg-rose-500/5',      border: 'border-rose-500/30',    pulse: false }
  return           { label: 'Extreme Fear', color: 'text-red-300',      bg: 'bg-red-900/10',      border: 'border-red-500/40',     pulse: true  }
}

function VolatilityCard({ v }: { v: OverviewVolatility }) {
  const regime = vixRegime(v.value)
  const up = (v.change ?? 0) >= 0

  return (
    <div className={clsx(
      'rounded-xl p-4 border flex flex-col gap-1',
      regime.bg, regime.border,
    )}>
      {/* Name + regime badge */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold text-text-primary truncate">{v.name}</p>
        <span className={clsx(
          'shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border',
          regime.color,
          regime.bg,
          regime.border,
          regime.pulse && 'animate-pulse',
        )}>
          {regime.label}
        </span>
      </div>

      {/* Description */}
      <p className="text-[11px] text-text-muted leading-tight">{v.description}</p>

      {/* Value — big */}
      <p className={clsx('text-3xl font-bold font-mono mt-2 leading-none', regime.color)}>
        {(v.value ?? 0).toFixed(2)}
      </p>

      {/* Change */}
      <p className={clsx('text-xs font-mono mt-1', up ? 'text-rose-400' : 'text-emerald-400')}>
        {/* VIX rising = more fear = red; VIX falling = calmer = green */}
        {up ? '+' : ''}{(v.change ?? 0).toFixed(2)} ({up ? '+' : ''}{(v.change_pct ?? 0).toFixed(2)}%)
      </p>
    </div>
  )
}

// ── Fear & Greed Card ───────────────────────────────────────────────────

function fgColor(value: number): { text: string; bar: string; bg: string; border: string } {
  if (value <= 25) return { text: 'text-red-400',     bar: 'bg-red-500',     bg: 'bg-red-500/5',     border: 'border-red-500/30'    }
  if (value <= 45) return { text: 'text-rose-400',    bar: 'bg-rose-500',    bg: 'bg-rose-500/5',    border: 'border-rose-500/30'   }
  if (value <= 55) return { text: 'text-amber-400',   bar: 'bg-amber-400',   bg: 'bg-amber-500/5',   border: 'border-amber-500/30'  }
  if (value <= 75) return { text: 'text-emerald-400', bar: 'bg-emerald-500', bg: 'bg-emerald-500/5', border: 'border-emerald-500/30'}
  return           { text: 'text-green-300',          bar: 'bg-green-400',   bg: 'bg-green-500/5',   border: 'border-green-500/30'  }
}

const FG_SEGMENTS = [
  { label: 'Extreme Fear', max: 25,  color: 'bg-red-600',     width: 'w-[25%]' },
  { label: 'Fear',         max: 45,  color: 'bg-rose-500',    width: 'w-[20%]' },
  { label: 'Neutral',      max: 55,  color: 'bg-amber-400',   width: 'w-[10%]' },
  { label: 'Greed',        max: 75,  color: 'bg-emerald-500', width: 'w-[20%]' },
  { label: 'Extreme Greed',max: 100, color: 'bg-green-400',   width: 'w-[25%]' },
]

function FearGreedCard({ fg }: { fg: OverviewFearGreed }) {
  const colors = fgColor(fg.value)
  const pct = Math.min(100, Math.max(0, fg.value))

  return (
    <section>
      <h2 className="panel-title flex items-center gap-2 mb-3">
        <Activity size={14} /> Fear &amp; Greed Index
      </h2>
      <div className={clsx('rounded-xl border p-5 flex flex-col gap-4 max-w-xl', colors.bg, colors.border)}>
        {/* Score + label */}
        <div className="flex items-center gap-5">
          <div className={clsx('flex flex-col items-center justify-center w-24 h-24 rounded-full border-4 shrink-0', colors.border)}>
            <span className={clsx('stat-hero font-mono leading-none', colors.text)}>{Math.round(fg.value)}</span>
            <span className="text-[10px] text-text-muted uppercase tracking-wide mt-1">/ 100</span>
          </div>
          <div>
            <p className={clsx('text-2xl font-bold', colors.text)}>{fg.label}</p>
            <p className="text-xs text-text-muted mt-1">CNN Fear &amp; Greed Index — market sentiment gauge</p>
            {/* Historical snapshots */}
            <div className="flex gap-4 mt-3">
              {fg.previous_close != null && (
                <div>
                  <p className="text-[10px] text-text-muted uppercase tracking-wide">Prev Close</p>
                  <p className="text-sm font-bold text-text-primary font-mono">{Math.round(fg.previous_close)}</p>
                </div>
              )}
              {fg.previous_1_week != null && (
                <div>
                  <p className="text-[10px] text-text-muted uppercase tracking-wide">1 Week Ago</p>
                  <p className="text-sm font-bold text-text-primary font-mono">{Math.round(fg.previous_1_week)}</p>
                </div>
              )}
              {fg.previous_1_month != null && (
                <div>
                  <p className="text-[10px] text-text-muted uppercase tracking-wide">1 Month Ago</p>
                  <p className="text-sm font-bold text-text-primary font-mono">{Math.round(fg.previous_1_month)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Segmented bar */}
        <div>
          <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
            {FG_SEGMENTS.map(seg => (
              <div
                key={seg.label}
                className={clsx('shrink-0 h-full rounded-sm', seg.color, seg.width)}
              />
            ))}
          </div>
          {/* Needle indicator — SVG so the x position is an attribute, not a style */}
          <svg width="100%" height="10" className="mt-1 overflow-visible" aria-hidden="true">
            <line
              x1={`${pct}%`} y1="0"
              x2={`${pct}%`} y2="10"
              strokeWidth="2"
              strokeLinecap="round"
              className={clsx(
                colors.bar === 'bg-red-500'     && '[stroke:theme(colors.red.500)]',
                colors.bar === 'bg-rose-500'    && '[stroke:theme(colors.rose.500)]',
                colors.bar === 'bg-amber-400'   && '[stroke:theme(colors.amber.400)]',
                colors.bar === 'bg-emerald-500' && '[stroke:theme(colors.emerald.500)]',
                colors.bar === 'bg-green-400'   && '[stroke:theme(colors.green.400)]',
              )}
            />
          </svg>
          {/* Labels */}
          <div className="flex justify-between mt-1">
            <span className="text-[9px] text-red-400 uppercase font-bold tracking-wide">Extreme Fear</span>
            <span className="text-[9px] text-amber-400 uppercase font-bold tracking-wide">Neutral</span>
            <span className="text-[9px] text-green-400 uppercase font-bold tracking-wide">Extreme Greed</span>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Sector Heatmap sub-component ─────────────────────────────────────────

function sectorBgClass(positive: boolean, intensity: number): string {
  // Map intensity (0–1) to one of 3 opacity steps per direction
  if (positive) {
    if (intensity > 0.66) return 'bg-emerald-500/20'
    if (intensity > 0.33) return 'bg-emerald-500/10'
    return 'bg-emerald-500/5'
  } else {
    if (intensity > 0.66) return 'bg-rose-500/20'
    if (intensity > 0.33) return 'bg-rose-500/10'
    return 'bg-rose-500/5'
  }
}

function SectorHeatmap({ sectors }: { sectors: OverviewSector[] }) {
  return (
    <section>
      <h2 className="panel-title flex items-center gap-2 mb-3">
        <TrendingUp size={14} /> S&amp;P Sector Performance
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
        {sectors.map(s => {
          const positive = (s.change_pct ?? 0) >= 0
          const intensity = Math.min(Math.abs(s.change_pct ?? 0) / 3, 1)
          return (
            <div
              key={s.ticker}
              className={clsx(
                'rounded-lg p-3 border flex flex-col gap-1 transition-all',
                positive
                  ? 'border-emerald-500/30 hover:border-emerald-500/60'
                  : 'border-rose-500/30 hover:border-rose-500/60',
                sectorBgClass(positive, intensity),
              )}
            >
              <p className="text-[10px] text-text-muted uppercase tracking-wide font-mono">{s.ticker}</p>
              <p className="text-xs font-semibold text-text-primary leading-tight">{s.name}</p>
              <p className={clsx(
                'text-base font-black font-mono mt-auto',
                positive ? 'text-emerald-400' : 'text-rose-400',
              )}>
                {positive ? '+' : ''}{(s.change_pct ?? 0).toFixed(2)}%
              </p>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ── Yield Curve Chart sub-component ─────────────────────────────────────

function YieldCurveChart({ points }: { points: OverviewYieldPoint[] }) {
  if (!points || points.length < 2) return null

  const W = 420
  const H = 140
  const PAD_L = 40
  const PAD_R = 16
  const PAD_T = 16
  const PAD_B = 28

  const yields = points.map(p => p.yield)
  const minY = Math.max(0, Math.min(...yields) - 0.3)
  const maxY = Math.max(...yields) + 0.3
  const rangeY = maxY - minY || 1

  const plotW = W - PAD_L - PAD_R
  const plotH = H - PAD_T - PAD_B

  function toX(i: number) {
    return PAD_L + (i / (points.length - 1)) * plotW
  }
  function toY(y: number) {
    return PAD_T + plotH - ((y - minY) / rangeY) * plotH
  }

  const polyline = points.map((p, i) => `${toX(i)},${toY(p.yield)}`).join(' ')

  // Detect inverted: any shorter tenor higher than a longer one
  const isInverted = yields[0] > yields[yields.length - 1]

  const lineColor = isInverted ? '#f87171' : '#34d399'  // rose-400 or emerald-400
  const dotColor  = isInverted ? '#f87171' : '#34d399'

  // Y-axis ticks: 4 evenly spaced
  const yTicks = Array.from({ length: 4 }, (_, i) => minY + (rangeY * i) / 3)

  return (
    <div className="mt-4 p-4 card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="panel-title flex items-center gap-2">
          <Landmark size={12} /> US Treasury Yield Curve
        </h3>
        <span className={clsx(
          'text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wide',
          isInverted
            ? 'text-rose-400 bg-rose-500/10 border-rose-500/30'
            : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
        )}>
          {isInverted ? 'INVERTED' : 'NORMAL'}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        aria-label="US Treasury Yield Curve"
        role="img"
      >
        {/* Y-axis gridlines + labels */}
        {yTicks.map((tick, i) => {
          const y = toY(tick)
          return (
            <g key={i}>
              <line
                x1={PAD_L} y1={y} x2={W - PAD_R} y2={y}
                stroke="currentColor" strokeWidth="0.5" className="text-border opacity-40"
              />
              <text
                x={PAD_L - 4} y={y + 4}
                textAnchor="end" fontSize="9"
                className="fill-current text-text-muted"
              >
                {tick.toFixed(2)}
              </text>
            </g>
          )
        })}

        {/* Shaded area under curve */}
        <polygon
          points={`${polyline} ${toX(points.length - 1)},${PAD_T + plotH} ${PAD_L},${PAD_T + plotH}`}
          fill={isInverted ? 'rgba(248,113,113,0.08)' : 'rgba(52,211,153,0.08)'}
        />

        {/* Curve line */}
        <polyline
          points={polyline}
          fill="none"
          stroke={lineColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points + labels */}
        {points.map((p, i) => {
          const x = toX(i)
          const y = toY(p.yield)
          const labelY = i % 2 === 0 ? y - 10 : y - 10
          return (
            <g key={p.tenor}>
              <circle cx={x} cy={y} r="4" fill={dotColor} />
              <text
                x={x} y={PAD_T + plotH + 14}
                textAnchor="middle" fontSize="9"
                className="fill-current text-text-muted"
              >
                {p.tenor}
              </text>
              <text
                x={x} y={labelY}
                textAnchor="middle" fontSize="9" fontWeight="600"
                fill={dotColor}
              >
                {(p.yield ?? 0).toFixed(2)}%
              </text>
            </g>
          )
        })}
      </svg>
      {/* Spread note */}
      <p className="text-[10px] text-text-muted mt-1">
        30Y–3M spread:{' '}
        <span className={clsx(
          'font-mono font-bold',
          (yields[yields.length - 1] - yields[0]) >= 0 ? 'text-emerald-400' : 'text-rose-400',
        )}>
          {((yields[yields.length - 1] - yields[0]) >= 0 ? '+' : '')}
          {(yields[yields.length - 1] - yields[0]).toFixed(3)}%
        </span>
        {isInverted && (
          <span className="ml-2 text-amber-400">
            Inverted curve — historically a recession indicator
          </span>
        )}
      </p>
    </div>
  )
}

// ── Generic Asset Tile sub-component ────────────────────────────────────

function AssetTile({ label, sublabel, value, change, changePct, onClick }: {
  label: string
  sublabel?: string
  value: string
  change: number | null
  changePct: number | null
  onClick: () => void
}) {
  const positive = (change ?? 0) >= 0
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'bg-surface-card border border-border rounded-lg p-3 text-left w-full',
        'hover:border-orange-500/40 hover-lift cursor-pointer',
      )}
    >
      <p className="text-xs font-bold text-text-secondary truncate">{label}</p>
      {sublabel && <p className="text-[10px] text-text-muted truncate">{sublabel}</p>}
      <p className="text-sm font-bold font-mono text-text-primary mt-1">{value}</p>
      <p className={clsx('text-[10px] font-mono mt-0.5', positive ? 'text-emerald-400' : 'text-rose-400')}>
        {fmtChange(change, 4)} ({fmtPct(changePct)})
      </p>
    </button>
  )
}
